import React, { useEffect, useState } from "react";
import { RECAPTCHA_SITE_KEY } from "@/config/api";

interface ReCaptchaWrapperProps {
  children: (token: string) => React.ReactNode;
  action: string;
}

export const ReCaptchaWrapper = ({ children, action }: ReCaptchaWrapperProps) => {
  const [reCaptchaToken, setReCaptchaToken] = useState<string>("");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    // Create and load the script
    const loadScript = () => {
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript) {
        setIsScriptLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setIsScriptLoaded(true);
      script.onerror = () => {
        console.error("reCAPTCHA script failed to load");
        // Set a fallback token to allow form submission
        setReCaptchaToken("fallback_token");
      };
      document.head.appendChild(script);
    };

    loadScript();

    // Cleanup
    return () => {
      const script = document.querySelector('script[src*="recaptcha"]');
      if (script && document.querySelectorAll('[data-recaptcha="true"]').length <= 1) {
        script.remove();
        setIsScriptLoaded(false);
      }
    };
  }, []);

  useEffect(() => {
    if (!isScriptLoaded) return;

    const executeReCaptcha = async () => {
      try {
        const grecaptcha = (window as any).grecaptcha;
        await grecaptcha.ready(async () => {
          try {
            const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
            setReCaptchaToken(token);
          } catch (error) {
            console.error("reCAPTCHA execution error:", error);
            setReCaptchaToken("fallback_token");
          }
        });
      } catch (error) {
        console.error("reCAPTCHA ready error:", error);
        setReCaptchaToken("fallback_token");
      }
    };

    // Set a timeout for reCAPTCHA execution
    const timeoutId = setTimeout(() => {
      if (!reCaptchaToken) {
        console.warn("reCAPTCHA timed out");
        setReCaptchaToken("fallback_token");
      }
    }, 5000);

    executeReCaptcha();

    return () => clearTimeout(timeoutId);
  }, [isScriptLoaded, action]);

  // Return the children immediately with a fallback token if there's an error
  if (reCaptchaToken === "fallback_token") {
    return (
      <>
        <div className="text-amber-500 text-sm text-center mb-4">
          Verification service encountered an error. You can proceed, but some features may be limited.
        </div>
        {children(reCaptchaToken)}
      </>
    );
  }

  // Show loading state only briefly
  if (!reCaptchaToken) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-gray-400">Initializing security verification...</div>
      </div>
    );
  }

  return (
    <div data-recaptcha="true">
      {children(reCaptchaToken)}
    </div>
  );
};
